import { useExtraData } from "app/hooks";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import ExtraOptionControls from "features/option-controls/extra-option-controls";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { OptionTemplate as OptionTemplateType } from "shared/api/middleware-api-client";
import OptionTemplate from "shared/ui/option-template";

import { OptionModalState } from "widgets/configurator/option-modal";
import { isVerticalProfileExtra } from "entities/configurator/model/staander";

const disableSelfAssemblyOption = (
  extraOptionCode: string,
  selectedDoor: OptionTemplateType,
) => {
  // For every self assembly door, disable "scharniervoorbereidingopdekdeur"
  if (extraOptionCode === "scharniervoorbereidingopdekdeur") {
    return true;
  }

  // For every self assembly opdekdeur, disable "bovenlichtrook"
  if (
    (extraOptionCode === "bovenlichtrook" ||
      extraOptionCode === "bovenlicht") &&
    selectedDoor.code === "opdekdeur"
  ) {
    return true;
  }

  // For every self assembly taatsdeur or schuifdeur, disable verticaalstelprofielleft and verticaalstelprofielright
  if (
    (extraOptionCode === "verticaalstelprofielleft" ||
      extraOptionCode === "verticaalstelprofielright") &&
    (selectedDoor.code === "taatsdeur" || selectedDoor.code === "schuifdeur")
  ) {
    return true;
  }

  return false;
};

type ExtraOptionsBlockType = {
  setOptionModalState: (state: OptionModalState) => void;
};

export const ExtraOptionsBlock = ({
  setOptionModalState,
}: ExtraOptionsBlockType) => {
  const { selfAssembleDoorData, selectedDoor, color, glass } =
    useConfigurationOptionsContext();

  const { includedExtraOptions, isExtraDataLoading } = useExtraData(
    selectedDoor?.code,
    color,
    glass,
  );

  if (!selectedDoor) return null;

  const newExtras =
    includedExtraOptions?.filter((option) => {
      // If the door is SelfAssembly => disable option Scharniervoorbereiding
      if (
        selfAssembleDoorData.selfAssembly &&
        disableSelfAssemblyOption(option.code, selectedDoor)
      ) {
        return false;
      }

      if (option.code.includes("breedte")) {
        return false;
      }
      if (isVerticalProfileExtra(option.code)) {
        return false;
      }
      return true;
    }) ?? [];

  if (isExtraDataLoading) return <Skeleton height={200} />;
  if (newExtras.length === 0) return null;
  return (
    <OptionTemplate
      title="Extra opties"
      className="gg-border-b-0"
      testId="door-extra-options"
      onShowInfoClick={() =>
        setOptionModalState({
          isOpen: true,
          heading: "Extra opties",
          description:
            "Heb je nog andere wensen? Hieronder vind je een aantal extra opties die je kunt toevoegen aan je " +
            "bestelling. Je kunt je wensen altijd nog aanpassen óf eerst op locatie bespreken met onze inmeter.",
        })
      }
    >
      <ExtraOptionControls
        extraOptions={newExtras}
        onShowExtraInfoClick={(option) =>
          setOptionModalState({
            isOpen: true,
            heading: option.name,
            description: option.description,
          })
        }
      />
    </OptionTemplate>
  );
};
